import { reportDocumentModelV1Schema, presentationSnapshotV1Schema,
  type ReportDocumentModelV1, type PresentationSnapshotV1 } from '../../types/reportContractsV2';
import { hashCanonicalJson } from '../reportGeneration/reportContractHash';
import { hashReportDocumentModelContent } from '../reportGeneration/reportDocumentIdentity';
import { hashReportSummaryModel, reportSummaryModelV1Schema, type ReportSummaryModelV1, type ReportSummaryEntry } from './types';
import { NUMBERED_REF_TOKEN_RE } from '../citationTokens';
import { presentReportReliabilityTokens } from '../reportRendering/presentReportReliabilityTokens';

export const REPORT_SUMMARY_NAVIGATION = [
  // The maintained native observation assigns physical page one to cover.
  // Bind the selected overview to that actual page instead of inventing a body-page anchor.
  { id: 'cover', title: 'Decision basis', group: 'overview' },
  { id: 'summaryFindings', title: 'Selected findings', group: 'analysis' },
  { id: 'summaryActionsSources', title: 'Actions, evidence gaps and sources', group: 'risks-actions' },
] as const;

/** Display-only citation omission; private quality/source records stay exact. */
export function presentReportSummaryCitations(model: ReportSummaryModelV1, value: string): string {
  const admitted = new Set(model.sources.map(source => source.index));
  return presentReportReliabilityTokens(value.replace(new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi'), token => {
    const originalIds = (token.match(/\d+/g) ?? []).map(Number);
    const kept = originalIds.filter(index => admitted.has(index));
    if (!kept.length) return '';
    if (kept.length === originalIds.length) return token;
    const inherited = /^(?:\[)(verified|inferred|unverified)\s+Ref/i.exec(token)?.[1];
    return token.slice(1, -1).split(',').flatMap(part => {
      const match = /^\s*(?:(verified|inferred|unverified)\s+)?(?:Ref\s*:?\s*)?(\d+)\s*$/i.exec(part);
      if (!match || !admitted.has(Number(match[2]))) return [];
      const qualifier = match[1] ?? inherited;
      return [`[${qualifier ? `${qualifier} ` : ''}Ref ${match[2]}]`];
    }).join(', ');
  }));
}

export function reportSummarySourceTexts(model: ReportSummaryModelV1): string[] {
  return model.sources.map(source => presentReportSummaryCitations(model, [`[Ref ${source.index}] ${source.label}`, source.field,
    source.qualification && source.qualification !== 'verified' && source.qualification !== 'inferred' ? `Qualification: ${source.qualification}` : '', source.sourceGrade ? `Source grade: ${source.sourceGrade}` : '',
    source.pageNumber == null ? '' : `Page ${source.pageNumber}`, source.dataAsOf ? `Data as of ${source.dataAsOf}` : '',
    source.retrievedAt ? `Retrieved ${source.retrievedAt}` : '', source.url ?? '',
    ...source.scopeDetails.map(scope => `${scope.topic}: ${scope.detail}`)].filter(Boolean).join(' · ')));
}

/** Whole gap descriptions only. The immutable model retains every complete
 * gap/detail; a bounded display never conceals the number of unresolved gaps. */
export function reportSummaryGapTexts(model: ReportSummaryModelV1): string[] {
  if (!model.gaps.length) return [];
  const selected: string[] = [];
  let remaining = 420;
  for (const gap of model.gaps) {
    const text = `${gap.title}: ${gap.reason.replace(/-/g, ' ')}. This evidence gap remains unresolved; consult the full report for its complete detail.`;
    if (selected.length >= 2 || text.length > remaining) continue;
    selected.push(text);
    remaining -= text.length;
  }
  const count = model.gaps.length;
  const sections = new Set(model.gaps.map(gap => gap.sectionId)).size;
  const unselected = count - selected.length;
  return [...selected, `${count} recorded evidence ${count === 1 ? 'gap remains' : 'gaps remain'} unresolved across ${sections} ${sections === 1 ? 'section' : 'sections'}.${unselected ? ` ${unselected} additional complete gap ${unselected === 1 ? 'description remains' : 'descriptions remain'} in the full report.` : ''} Consult the complete saved report for every qualification and gap detail.`];
}

/** Selection omissions are not new findings. Keep all per-section records in
 * the hashed model and disclose their actual complete-entry and section totals. */
export function reportSummaryOmissionTexts(model: ReportSummaryModelV1): string[] {
  if (!model.omittedSelections.length) return [];
  const count = model.omittedSelections.reduce((total, row) => total + row.count, 0);
  const sections = new Set(model.omittedSelections.map(row => row.sectionId)).size;
  return [`${count} additional complete source ${count === 1 ? 'entry remains' : 'entries remain'} across ${sections} ${sections === 1 ? 'section' : 'sections'} in the full report. This selection retains whole source units; consult the complete saved report for their remaining detail and qualifications.`];
}

/** Consecutive units from the exact same source section share one visible
 * heading. Every original entry/text remains in order; equal labels from
 * different sections and nonconsecutive sections keep their own headings. */
export function reportSummaryEntryHeadings(entries: readonly ReportSummaryEntry[]): string[] {
  return entries.map((entry, index) => {
    const previous = entries[index - 1];
    return previous?.sectionId === entry.sectionId && previous.sectionTitle === entry.sectionTitle
      ? '' : entry.sectionTitle;
  });
}

export const REPORT_SUMMARY_FULL_REPORT_BOUNDARY = 'This is a selected three-page summary. Consult the complete saved report for its evidence, qualifications and remaining detail.';

/** The visible review phrases are also the canonical selected inventory.
 * Humanize only the existing machine verdict label; retain complete frozen
 * findings and delivery reasons without changing their authority or state. */
export function reportSummaryQualityTexts(model: ReportSummaryModelV1): {
  heading: string; status: string[]; validationMethod?: string; notes: string[];
} {
  const quality = model.qualityDecision;
  const humanize = (value: string): string => value.replace(/[-_]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\s+/g, ' ').trim();
  return {
    heading: 'Quality review',
    status: [`Quality ${humanize(quality.state)}`, `Hard gates ${quality.hardGatesPassed ? 'passed' : 'failed'}`,
      ...(quality.reviewerVerdict ? [`Reviewer verdict ${humanize(quality.reviewerVerdict)}`] : [])],
    ...(quality.validationMethod === 'deterministic-source-screening'
      ? { validationMethod: 'Validation method: deterministic source-bound screening; no model reviewer verdict or community-support score.' } : {}),
    notes: [...quality.findings, ...model.deliveryDecision.reasons.map(reason => reason.message)],
  };
}

/** Selected inventory only. The complete source document and artifacts remain intact. */
export function buildReportSummaryDocumentV1(value: ReportSummaryModelV1, source: ReportDocumentModelV1): ReportDocumentModelV1 {
  const model = reportSummaryModelV1Schema.parse(value);
  if (model.modelHash !== hashReportSummaryModel(model)
    || model.identity.reportId !== source.reportId || model.identity.revisionId !== source.revisionId
    || model.identity.sourceDocumentHash !== source.contentHash || model.identity.reportType !== source.reportType
    || model.identity.siteId !== source.siteId || hashReportDocumentModelContent(source) !== source.contentHash
    || hashCanonicalJson(model.qualityDecision) !== hashCanonicalJson(source.qualityDecision)
    || hashCanonicalJson(model.demonstrationDisclosure ?? null) !== hashCanonicalJson(source.demonstrationDisclosure ?? null)) {
    throw new Error('The derived summary does not match the frozen canonical source.');
  }
  const review = reportSummaryQualityTexts(model);
  const quality = [review.heading, ...review.status, review.validationMethod ?? '', ...review.notes];
  const extras: string[][] = [
    [model.reportTypeLabel, model.summary, model.recommendation ? `Report recommendation: ${model.recommendation}` : '', ...quality,
      REPORT_SUMMARY_FULL_REPORT_BOUNDARY, `Report ID ${model.identity.reportId}`,
      `Version ${model.identity.sourceVersion.major}.${model.identity.sourceVersion.minor}`, model.identity.sourceGeneratedAt,
      model.demonstrationDisclosure?.notice ?? ''],
    [],
    [...reportSummaryGapTexts(model), ...reportSummarySourceTexts(model),
      ...reportSummaryOmissionTexts(model),
      ...model.legalNotice,
      model.demonstrationDisclosure?.notice ?? ''],
  ];
  const sections = REPORT_SUMMARY_NAVIGATION.map(({ id, title, group }, index) => ({
    id, sectionId: id, title, group, role: 'narrative' as const, required: true,
    presentationPolicy: { surfaceEligibility: { privateView: true, publicView: false, pdf: true, docx: false, pptx: false, csv: false },
      preferredSpan: 'full-page-width' as const, splittable: false,
      keepRules: { headingWithFollowingLines: 3, captionWithFigure: true, repeatTableHeader: true, keepRowsIntact: true },
      continuationLabel: `${title} (continued)`, expectedExhibits: [], omissionPolicy: 'block-if-missing' as const },
    blocks: [{ id: `${id}:selected`, kind: 'list' as const, ordered: false,
      items: [...reportSummaryEntryHeadings(model.pages[index]!.entries).flatMap((heading, entryIndex) => [heading, model.pages[index]!.entries[entryIndex]!.text]), ...extras[index]!].filter(Boolean)
        .map((text, itemIndex) => ({ id: `${id}:item:${itemIndex}`, text: presentReportSummaryCitations(model, text) })), sourceIds: [], citationIds: [] }],
  }));
  const withoutIdentity = { ...source, title: model.title, summary: model.summary, sections,
    omissions: [], claims: [], citations: [], evidence: {}, assets: [] };
  const provisional = reportDocumentModelV1Schema.parse({ ...withoutIdentity, contentHash: source.contentHash });
  return reportDocumentModelV1Schema.parse({ ...provisional, contentHash: hashReportDocumentModelContent(provisional) });
}

export function deriveFrozenReportSummaryPresentation(frozen: PresentationSnapshotV1, pageSize: 'A4' | 'Letter'): PresentationSnapshotV1 {
  const source = presentationSnapshotV1Schema.parse(frozen);
  const { presentationHash, ...content } = source;
  if (hashCanonicalJson(content) !== presentationHash) throw new Error('The summary presentation source failed its hash check.');
  const next = { ...content, pageSize, themeId: 'house-editorial-v1', marginsMm: { top: 14, right: 14, bottom: 16, left: 14 },
    fonts: { display: 'Fraunces', body: 'Newsreader', ui: 'Hanken Grotesk' },
    sectionOrder: REPORT_SUMMARY_NAVIGATION.map(section => section.id), sectionColumnLayouts: {} };
  return presentationSnapshotV1Schema.parse({ ...next, presentationHash: hashCanonicalJson(next) });
}
